import React from 'react';
import { Control, Controller, FieldPath, FieldValues, RegisterOptions } from 'react-hook-form';

import { Box, TextField, Typography, useTheme } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

type LabelledDatePickerProps<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>> = {
  label: string;
  name: TName;
  control: Control<TFieldValues>;
  rules?: Omit<RegisterOptions<TFieldValues, TName>, 'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'>;
  disabled?: boolean;
  dataTestId?: string;
  textFieldSize?: 'small' | 'medium';
};

const LabelledDatePicker = <TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>({
  label,
  name,
  control,
  rules,
  disabled,
  dataTestId,
  textFieldSize = 'small',
}: LabelledDatePickerProps<TFieldValues, TName>) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        pointerEvents: disabled ? 'none' : 'auto',
      }}>
      <Typography variant="body2">{label}</Typography>
      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ field: { value = null, onChange }, fieldState: { error } }) => (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              disabled={disabled}
              value={value}
              onChange={onChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size={`${textFieldSize}`}
                  error={!!error}
                  helperText={error?.message}
                  fullWidth
                  data-testid={dataTestId}
                  sx={{
                    backgroundColor: disabled ? theme.palette.grey[200] : 'inherit',
                  }}
                />
              )}
            />
          </LocalizationProvider>
        )}
      />
    </Box>
  );
};

export default LabelledDatePicker;
